// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logomark } from '../ui/Logomark.js';
import { useSearchParams } from 'react-router-dom';
import { loginUser, getKeyBundle } from '../../lib/syncClient.js';
import {
  deriveAuthTokenViaWorker,
  unwrapDekViaWorker,
} from '../../store/useCryptoWorker.js';
import { useApp } from '../../store/AppContext.js';
import { ServerUrlField } from './ServerUrlField.js';
import { LanguageSwitcher } from '../settings/LanguageSwitcher.js';
import { BUILD_STAMP } from '../../lib/appVersion.js';

interface LoginFormProps {
  syncUrl: string;
  worker: Worker;
  onSwitchToRegister: () => void;
  onSwitchToRecover: () => void;
}

export function LoginForm({ syncUrl, worker, onSwitchToRegister, onSwitchToRecover }: LoginFormProps) {
  const { t } = useTranslation(['auth', 'common']);
  const { dispatch } = useApp();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('expired') === '1';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!username.trim()) { setError(t('login.usernameRequired')); return; }
    if (!password) { setError(t('login.passwordRequired')); return; }

    setLoading(true);
    try {
      setLoadingStep(t('login.deriving'));
      const serverAuthToken = await deriveAuthTokenViaWorker(worker, password, username.trim());

      setLoadingStep(t('login.signingIn'));
      const result = await loginUser(syncUrl, username.trim(), serverAuthToken);

      setLoadingStep(t('login.fetchingKeys'));
      const bundle = await getKeyBundle({ baseUrl: syncUrl, token: result.token });

      setLoadingStep(t('login.unlockingKey'));
      // Unwrap the DEK with the password KEK (Argon2id, params from the bundle).
      const keyData = await unwrapDekViaWorker(worker, bundle.wrappedByPassword, password, bundle.kdfParams);

      dispatch({
        type: 'AUTH_SUCCESS',
        username: username.trim(),
        token: result.token,
        refreshToken: result.refreshToken,
        keyData,
        syncUrl,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.loginFailed'));
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo / brand */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-2xl font-semibold text-text mb-1">
            <Logomark size={24} />
            {t('common:appName')}
          </div>
          <p className="text-sm text-text-muted">{t('common:tagline')}</p>
        </div>

        <div className="bg-bg-surface border border-border p-6">
          <h2 className="text-md font-semibold text-text mb-5">{t('login.signIn')}</h2>

          {sessionExpired && !error && (
            <div className="border border-warning text-warning px-3 py-2 mb-4 text-sm">
              {t('login.sessionExpired')}
            </div>
          )}

          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-text-muted mb-1">{t('login.username')}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none placeholder-text-subtle"
                autoComplete="username"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm text-text-muted mb-1">{t('login.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none"
                autoComplete="current-password"
              />
            </div>

            <ServerUrlField />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-accent text-white py-2.5 text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors"
            >
              {loading ? (loadingStep || t('login.signingIn')) : t('login.signIn')}
            </button>
          </form>
        </div>

        <div className="mt-4 flex gap-6 text-sm text-text-muted px-1">
          <button onClick={onSwitchToRegister} className="hover:text-text transition-colors">
            {t('login.createAccount')}
          </button>
          <button onClick={onSwitchToRecover} className="hover:text-text transition-colors">
            {t('login.recoverWithPhrase')}
          </button>
        </div>

        <div className="mt-4 flex justify-center px-1">
          <LanguageSwitcher
            worker={worker}
            className="bg-transparent border-none text-text-subtle text-xs px-1 py-1 focus:outline-none hover:text-text-muted cursor-pointer"
          />
        </div>

        {/* Build stamp, visible pre-auth — exactly where people are when they
            need it for a bug report. Brand name + semver, so no i18n needed. */}
        <p className="mt-2 text-center text-xs text-text-subtle">Hushold {BUILD_STAMP}</p>
      </div>
    </div>
  );
}
